import { http } from '@pendo/http';
import { i18n_t as $t } from '@/utils/i18n';

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['streaming', 'revision', 'force'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function getAdoptionInsights (requestBody, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/insight/generate', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, requestBody, requestOptions).then((res) => res.data);
}

export function getInitialState () {
    return {
        insights: null,
        isLoading: false,
        error: null,
    };
}

export function determineAppId (rootGetters) {
    // Remove this once we have config blade pulling the app id
    const defaultApp = rootGetters['apps/allWebPlusIntegratedMobileApps']?.[0];
    if (defaultApp?.id) {
        return defaultApp.id;
    }

    return -323232;
}

export function buildRequestBody (appId, countableIds = [], includeAnonymous = false) {
    return {
        job_spec: {
            app_id: appId,
            metrics: [
                { metric_type:'adoption_new_users' },
                { metric_type:'adoption_all_users_increase' },
                { metric_type:'adoption_all_users_decrease' },
            ],
            offset_days: 30,
            trend_type: 'adoption',
            countable_ids: countableIds,
            include_anonymous: includeAnonymous,
        },
    };
}

export function buildAppDataFromAdoptionState (state, rootGetters, fallbackAppId = null) {
    const adoptionInsightsArtifact = state?.artifacts?.adoption_insights;
    const responseAppId = adoptionInsightsArtifact?.data?.app_id || fallbackAppId;

    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(responseAppId);

    const appData = {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId: responseAppId,
    };

    return appData;
}

function parseSummary (summary) {
    if (!summary || typeof summary !== 'string') {
        return { textBefore: summary || '', textAfter: '', showAppTag: false };
    }

    const tagNamePlaceholder = '{{COUNTABLE_NAME}}';
    const placeholderIndex = summary.indexOf(tagNamePlaceholder);

    if (placeholderIndex !== -1) {
        const textBefore = summary.substring(0, placeholderIndex).trim();
        const textAfter = summary.substring(placeholderIndex + tagNamePlaceholder.length).trim();

        return { textBefore, textAfter, showAppTag: true };
    }

    return { textBefore: summary, textAfter: '', showAppTag: false };
}

function getCountableIcon (countableKind) {
    const kindMap = {
        Feature: 'mouse-pointer',
        Page: 'file',
    };

    return kindMap[countableKind] || 'feature';
}

function getTitle (metric, countableKind) {
    if (metric === 'adoption_all_users') {
        const kindTitleMap = {
            Feature: $t('trends.Feature adoption'),
            Page: $t('trends.Page adoption'),
            TrackType: $t('trends.Event adoption'),
        };

        return kindTitleMap[countableKind] || $t('trends.Adoption');
    }

    if (metric === 'adoption_new_users') {
        const kindTitleMap = {
            Feature: $t('trends.Feature adoption'),
            Page: $t('trends.Page adoption'),
            TrackType: $t('trends.Event adoption'),
        };

        return kindTitleMap[countableKind] || $t('trends.Adoption');
    }

    return $t('trends.Adoption');
}

export function transformInsightsResponse (finalState, appData) {
    const adoptionInsightsArtifact = finalState?.artifacts?.adoption_insights;
    const adoptionInsightsData = adoptionInsightsArtifact?.data;
    if (!adoptionInsightsData?.results || !Array.isArray(adoptionInsightsData.results)) {
        return [];
    }

    const rawIcon = appData?.icon;
    const appIcon = typeof rawIcon === 'string'
        ? { type: rawIcon, size: 12 }
        : { ...rawIcon, size: 12 };

    return adoptionInsightsData.results.map((result) => {
        const { metric, countable_name: countableName, countable_kind: countableKind } = result;

        const tagName = countableName;
        const countableIcon = getCountableIcon(countableKind);
        const tagIcon = countableIcon ? { type: countableIcon, size: 12 } : appIcon;

        const title = getTitle(metric, countableKind);

        const { textBefore, textAfter, showAppTag } = parseSummary(result.summary, tagName);

        return {
            title,
            textBeforeTagName: textBefore,
            textAfterTagName: textAfter,
            showAppTag,
            tagName,
            tagIcon,
            appName: appData?.name,
            appIcon,
            delta: result.adoption_rate_change,
            trendDirection: result.trend_direction,
            adoptionRateCurrent: result.adoption_rate_current,
            adoptionRatePrevious: result.adoption_rate_previous,
            countableId: result.countable_id,
            countableKind,
            metric,
            type: 'adoption',
            appId: appData?.appId,
        };
    }).filter(Boolean);
}
